import React, { useState } from 'react';

import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { Box, Grid } from '@atlaskit/primitives/compiled';

import type { ConfluencePageContributor } from '../../../../api/useTwgSources/gql/types';
import { messages } from '../messages';

import { Contributors } from './contributors/Contributors';
import { RelatedLinks } from './related-links/RelatedLinks';

type AdditionalInfoSectionProps = {
	links: string[];
	contributors: ConfluencePageContributor[];
	cloudId: string;
	onResourceClick: ({
		event,
		resourceUrl,
		location,
	}: {
		event: React.MouseEvent | React.KeyboardEvent;
		resourceUrl: string;
		location: string;
	}) => void;
	onContributorClick: (action: string) => void;
	onExpandClicked: (layout: 'single-column' | 'dual-column') => void;
};

const styles = cssMap({
	showLessButtonContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	dualColumnLayout: {
		gridTemplateColumns: '1fr 1fr',
	},
});

export const AdditionalInfoSection = ({
	links,
	contributors,
	cloudId,
	onResourceClick,
	onContributorClick,
	onExpandClicked,
}: AdditionalInfoSectionProps): React.JSX.Element | null => {
	const [isExpanded, setIsExpanded] = useState(false);
	let layout: 'single-column' | 'dual-column' = 'single-column';

	const handleExpandToggle = () => {
		if (!isExpanded) {
			onExpandClicked(layout);
		}
		setIsExpanded(!isExpanded);
	};

	if (links.length === 0 && contributors.length === 0) {
		return null;
	}

	// we need to decide the layout based on the number of items
	// if we have lesser that 5 links and contributors each, we need to show them in a dual column
	// if we have only one of them, we need to show them in a single column
	// if we have more than 5 links or contributors, we need to show them in a single column
	// of the above: we show links first and then contributors if there are no links
	if (
		links.length > 0 &&
		links.length <= 5 &&
		contributors.length > 0 &&
		contributors.length <= 5
	) {
		layout = 'dual-column';
	}

	return (
		<>
			{layout === 'dual-column' ? (
				<Grid columnGap="space.400" xcss={styles.dualColumnLayout}>
					<Box>
						<RelatedLinks
							links={links}
							singleColumnLayout
							isExpanded={isExpanded}
							onExpandToggle={handleExpandToggle}
							onResourceClick={onResourceClick}
						/>
					</Box>
					<Box>
						<Contributors
							singleColumnLayout
							contributors={contributors}
							cloudId={cloudId}
							isExpanded={isExpanded}
							onExpandToggle={handleExpandToggle}
							onContributorClick={onContributorClick}
						/>
					</Box>
				</Grid>
			) : links.length > 0 ? (
				<RelatedLinks
					links={links}
					isExpanded={isExpanded}
					onExpandToggle={handleExpandToggle}
					onResourceClick={onResourceClick}
				/>
			) : (
				<Contributors
					contributors={contributors}
					isExpanded={isExpanded}
					onContributorClick={onContributorClick}
					onExpandToggle={handleExpandToggle}
					cloudId={cloudId}
				/>
			)}
			{isExpanded && (
				<Box xcss={styles.showLessButtonContainer}>
					<Button appearance="subtle" onClick={() => setIsExpanded(false)}>
						<FormattedMessage {...messages.show_less_button_label} />
					</Button>
				</Box>
			)}
		</>
	);
};
